// src/features/roadmap/workflowDefinitions.js
//
// ROADMAP-WORKFLOW-VIEW-S1: Data-driven workflow config for the Roadmap
// Workflow view. Adding a new object means adding one entry here — no new
// component code required.
//
// Schema per entry:
//   object      — display name
//   sources     — [{name, status: 'live'|'partial'|'gap', subtitle}]
//   steps       — [{n, title, desc, actor: 'ai'|'human'|'system',
//                   status: 'live'|'partial'|'mock'|'not_built'|'concept',
//                   connectedObjects: [{label, wired}]}]
//   insight     — amber weakest-link sentence
//   governance  — string[] (three governed statements)
//
// Omitting an object slug from this map → "Not yet mapped" empty state.

export const WORKFLOW_DEFINITIONS = {
  leases: {
    object: "Leases",

    sources: [
      {
        name: "Manual entry",
        status: "live",
        subtitle: "Lease data entered directly by the portfolio team",
      },
      {
        name: "Spacebase import",
        status: "partial",
        subtitle: "Automated import partial - some fields unmapped",
      },
      {
        name: "Recurring lease report",
        status: "partial",
        subtitle: "Monthly landlord reports parsed - ~70 % coverage",
      },
      {
        name: "PDF lease abstraction",
        status: "not_built",
        subtitle: "AI-assisted PDF extraction not yet built",
      },
    ],

    steps: [
      {
        n: 1,
        title: "Ingest",
        desc: "Lease record enters the system via manual entry, import, or document upload.",
        actor: "system",
        status: "partial",
        connectedObjects: [],
      },
      {
        n: 2,
        title: "Abstract",
        desc: "AI extracts key terms - rent, area, break clauses, and critical dates - from the lease document.",
        actor: "ai",
        status: "not_built",
        connectedObjects: [],
      },
      {
        n: 3,
        title: "Validate",
        desc: "Portfolio manager reviews extracted fields and confirms accuracy against the signed document.",
        actor: "human",
        status: "partial",
        connectedObjects: [],
      },
      {
        n: 4,
        title: "Link",
        desc: "Lease linked to its location record and any active signals or projects.",
        actor: "human",
        status: "live",
        connectedObjects: [
          { label: "Locations", wired: true },
          { label: "Projects",  wired: false },
        ],
      },
      {
        n: 5,
        title: "Track",
        desc: "System monitors critical dates - expiry, break options, rent reviews - and flags upcoming events.",
        actor: "system",
        status: "live",
        connectedObjects: [{ label: "Events", wired: false }],
      },
      {
        n: 6,
        title: "Renew",
        desc: "Renewal or exit decision is recorded and outcomes are logged against the lease record.",
        actor: "human",
        status: "not_built",
        connectedObjects: [],
      },
    ],

    insight:
      "Thin at BOTH ends - PDF abstraction (Step 2) is not built so lease terms arrive incomplete, and the renewal decision loop (Step 6) is also absent, meaning critical-date outcomes are never formally recorded.",

    governance: [
      "Lease financial terms are sensitive - read-only access is enforced on all non-portfolio-team surfaces.",
      "Every change to a lease record must generate a full audit entry with actor, timestamp, and prior value.",
      "Lease data surfaced in dashboards and reports is read-only; amendments must be made in the lease management workflow.",
    ],
  },

};

// Ordered list of all objects shown in the selector (mapped first, then
// unmapped). The selector always shows all five regardless of definition
// coverage so the empty state is reachable without code changes.
export const WORKFLOW_OBJECT_SLUGS = [
  { slug: "leases",   label: "Leases"   },
  { slug: "projects", label: "Projects" },
];
